/**
 * The global error store is used to gracefully handle top-level errors in Opal.
 *
 * React Router encourages throwing exceptions whenever something goes wrong so that
 * their error boundary can fall back to a friendly UI for the user. That works well,
 * but the underlying components that were being displayed will no longer be rendered.
 * This means that React Router can only display full-screen error messaging.
 *
 * That's great as a fallback for more unknown or catastrophic issues, but we have a
 * handful of situations where we can offer the user up a modal or
 * some other less-jarring UI.
 *
 * This store can be used inside and outside of react components to declare error
 * states and have the UI respond accordingly without needing to raise exceptions.
 */
import { useStore } from 'zustand'
import { createStore } from 'zustand/vanilla'

/**
 * A set of known error codes to be gracefully handled by the UI instead of being
 * thrown as exceptions.
 */
type KnownErrorCodes = 'opal_version_mismatch' | 'user_session_expired'

interface GlobalErrorState {
  errorCode: KnownErrorCodes | null
  setErrorCode: (code: KnownErrorCodes | null) => void
}

/**
 * Creating store as a non-react hook so
 * it can be imported and used from outside of hooks if needed.
 */
export const globalErrorStore = createStore<GlobalErrorState>()((set) => ({
  errorCode: null,
  setErrorCode: (errorCode) => set(() => ({ errorCode: errorCode })),
}))

/**
 * Creating hook interfaces for use within components
 */
export const useGlobalErrorCode = () =>
  useStore(globalErrorStore, (state) => state.errorCode)

export const useSetGlobalErrorCode = () =>
  useStore(globalErrorStore, (state) => state.setErrorCode)
